/**
 * BoxConnector — host fleet-managed Box filesystem connector.
 *
 * Cloud mounts are prepared outside the agent container and exposed through a
 * bind mount. The package-side driver only verifies the bind and returns a
 * no-op lifecycle handle.
 */

import type {
  ConnectContext,
  ConnectorDeclaration,
  ConnectorHandle,
  FilesystemFace,
  SyncOptions,
  SyncResult,
} from "@skaile/workspaces/connectors";
import {
  AbstractConnector,
  ensureFleetMounted,
  isFleetManaged,
} from "@skaile/workspaces/connectors";

interface BoxState {
  fleetManaged: true;
}

const CLOUD_FLEET_REQUIRED =
  "Box cloud connectors require host fleet-managed mounts. Add this connector id to SKAILE_FLEET_MANAGED_MOUNTS and bind the host-managed mount into the workspace container.";

/**
 * Filesystem-face connector for host fleet-managed Box mounts.
 * @docLink packages/factory-assets/concepts#box-connector
 */
export class BoxConnector extends AbstractConnector {
  readonly name = "box";

  readonly filesystem: FilesystemFace = {
    sync: (_handle: ConnectorHandle, options?: SyncOptions): Promise<SyncResult> =>
      syncFleetManaged(options),
  };

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    const targetDir = ctx.mountTarget;
    if (!targetDir) {
      throw new Error("BoxConnector requires ctx.mountTarget to be set by the manager");
    }
    if (!isFleetManaged(declaration.id)) {
      throw new Error(CLOUD_FLEET_REQUIRED);
    }

    this.log.info("mount handled by host fleet", {
      connectorId: declaration.id,
      targetDir,
    });
    await ensureFleetMounted(targetDir, this.log);
    return this.makeHandle(declaration, { fleetManaged: true } satisfies BoxState, targetDir);
  }

  async disconnect(_handle: ConnectorHandle): Promise<void> {}

  override async onHibernate(handle: ConnectorHandle): Promise<string | undefined> {
    await this.filesystem.sync(handle);
    return "flushed";
  }

  override async onSessionClose(handle: ConnectorHandle): Promise<string | undefined> {
    await this.filesystem.sync(handle);
    return "flushed";
  }
}

/**
 * Creates a new BoxConnector instance.
 * @docLink packages/factory-assets/api-reference#box-connector-factory
 */
export function createConnector(): BoxConnector {
  return new BoxConnector();
}

function syncFleetManaged(options?: SyncOptions): Promise<SyncResult> {
  options?.onSyncStatus?.({
    phase: "initial_sync_complete",
    totalFiles: 0,
    downloadedFiles: 0,
    stubFiles: 0,
    message: "fleet-managed mount — flush handled by host",
  });
  return Promise.resolve({ filesWritten: 0, filesDeleted: 0, bytesTransferred: 0 });
}
